import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Save, Globe, Mail, Phone, MapPin, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCMSSettings() {
  const [settings, setSettings] = useState({
    site_name: '',
    tagline: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    social_links: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cms/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        site_name: response.data.site_name || '',
        tagline: response.data.tagline || '',
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
        address: response.data.address || '',
        social_links: response.data.social_links || {
          linkedin: '',
          twitter: '',
          facebook: '',
          instagram: ''
        }
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Settings saved successfully' });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSocialChange = (platform, value) => {
    setSettings({
      ...settings,
      social_links: {
        ...settings.social_links,
        [platform]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E285B]"></div>
      </div>
    );
  }

  return (
    <div data-testid="cms-settings-container">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B]">Site Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Configure global site settings and contact information</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* General Settings */}
        <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="font-semibold text-[#0E285B] mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-[#F5A623]" />
            General Settings
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site Name</label>
              <input
                type="text"
                value={settings.site_name}
                onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-site-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tagline</label>
              <input
                type="text"
                value={settings.tagline}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-tagline"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="font-semibold text-[#0E285B] mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#F5A623]" />
            Contact Information
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Mail className="h-4 w-4 inline mr-1" />
                Email
              </label>
              <input
                type="email"
                value={settings.contact_email}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Phone className="h-4 w-4 inline mr-1" />
                Phone
              </label>
              <input
                type="text"
                value={settings.contact_phone}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <MapPin className="h-4 w-4 inline mr-1" />
                Address
              </label>
              <textarea
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-address"
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="font-semibold text-[#0E285B] mb-4 flex items-center gap-2">
            <Link2 className="h-5 w-5 text-[#F5A623]" />
            Social Media Links
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">LinkedIn</label>
              <input
                type="url"
                value={settings.social_links?.linkedin || ''}
                onChange={(e) => handleSocialChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/company/..."
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-linkedin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Twitter / X</label>
              <input
                type="url"
                value={settings.social_links?.twitter || ''}
                onChange={(e) => handleSocialChange('twitter', e.target.value)}
                placeholder="https://twitter.com/..."
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-twitter"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Facebook</label>
              <input
                type="url"
                value={settings.social_links?.facebook || ''}
                onChange={(e) => handleSocialChange('facebook', e.target.value)}
                placeholder="https://facebook.com/..."
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-facebook"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Instagram</label>
              <input
                type="url"
                value={settings.social_links?.instagram || ''}
                onChange={(e) => handleSocialChange('instagram', e.target.value)}
                placeholder="https://instagram.com/..."
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="settings-instagram"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#0E285B] hover:bg-[#0E285B]/90"
            data-testid="settings-save-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
